<template>
  <GridCol v-if="!$auth.loading" :sm="3" :md="2" class="sidebar" style="overflow-x: hidden">
    <div v-if="projectId">
      <ul class="nav nav-sidebar nav-top-border">
        <li v-if="userHasMultipleProjects">
          <router-link :to="{ name: 'collections-home' }" data-intercom-target="Projects Home">
            <i class="glyphicon glyphicon-home" /> All Projects
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'project-home' }" data-intercom-target="Sidebar Home">
            <i class="fa fa-folder-open" /> Project Overview
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'inventory' }" data-intercom-target="Sidebar Inventory">
            <i class="glyphicon glyphicon-book" /> Catalog
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'project-perf' }">
            <i class="glyphicon glyphicon-dashboard" /> Performance
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'conformance-summary' }">
            <i class="glyphicon glyphicon-ok" /> Conformance
          </router-link>
        </li>
      </ul>

      <div class="nav-sidebar-con b-border">
        <div
          class="nav-l1"
          @click="toggleList('investigate')"
          @keyup.enter="toggleList('investigate')"
        >
          <h5>Investigate</h5>
          <div
            class="caret"
            :style="
              visibleLists.investigate ? 'transform: rotate(180deg)' : 'transform: rotate(0deg)'
            "
          />
        </div>
        <ul :class="{ open: visibleLists.investigate }" class="nav nav-sidebar nav-sidebar-list">
          <li v-if="showProjectStats">
            <router-link
              :to="{ name: 'project-stats' }"
              data-intercom-target="Sidebar Project Stats"
            >
              <i class="glyphicon glyphicon-signal" /> Project Stats
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'reports-home' }" data-intercom-target="Sidebar Reports">
              <i class="glyphicon glyphicon-list-alt" /> Status Reports
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'insights' }" data-intercom-target="Sidebar Insights">
              <i class="glyphicon glyphicon-hourglass" /> Quality Analytics
            </router-link>
          </li>
          <li>
            <a
              :href="getPath({ name: 'notifications_home' })"
              data-intercom-target="Sidebar Notifications"
            >
              <i class="glyphicon glyphicon-bell" /> Issue Notifications
            </a>
          </li>
          <li>
            <router-link :to="{ name: 'results-home' }" data-intercom-target="Sidebar Results">
              <i class="glyphicon glyphicon-check" /> All Call Results
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-sidebar-con b-border">
        <div
          class="nav-l1"
          @click="toggleList('monitoring')"
          @keyup.enter="toggleList('monitoring')"
        >
          <h5>Configure Monitoring</h5>
          <div
            class="caret"
            :style="
              visibleLists.monitoring ? 'transform: rotate(180deg)' : 'transform: rotate(0deg)'
            "
          />
        </div>
        <ul :class="{ open: visibleLists.monitoring }" class="nav nav-sidebar nav-sidebar-list">
          <li>
            <router-link :to="{ name: 'calls-home' }" data-intercom-target="Sidebar Calls">
              <i class="glyphicon glyphicon-list" /> API Calls
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'workflows-home' }" data-intercom-target="Sidebar Workflows">
              <i class="glyphicon glyphicon-tasks" /> Workflows
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'schedules-home' }" data-intercom-target="Sidebar Schedules">
              <i class="glyphicon glyphicon-time" /> Schedules
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'webhooks-home' }" data-intercom-target="Sidebar Webhooks">
              <i class="glyphicon glyphicon-globe" /> Alerts &amp; Webhooks
            </router-link>
          </li>
          <li v-if="isProjectEditor">
            <router-link :to="{ name: 'auths-home' }" data-intercom-target="Sidebar Auth">
              <i class="glyphicon glyphicon-lock" /> API Authentication
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-sidebar-con b-border">
        <div class="nav-l1" @click="toggleList('settings')" @keyup.enter="toggleList('settings')">
          <h5>Project Settings</h5>
          <div
            class="caret"
            :style="visibleLists.settings ? 'transform: rotate(180deg)' : 'transform: rotate(0deg)'"
          />
        </div>
        <ul :class="{ open: visibleLists.settings }" class="nav nav-sidebar nav-sidebar-list">
          <li>
            <router-link
              :to="{ name: 'assertions-http' }"
              data-intercom-target="Sidebar Project HTTP status"
            >
              <i class="glyphicon glyphicon-transfer" /> Project Conditions: HTTP
            </router-link>
          </li>
          <li>
            <router-link
              :to="{ name: 'assertions-headers' }"
              data-intercom-target="Sidebar Project Header variables"
            >
              <i class="glyphicon glyphicon-cog" /> Project Conditions: Header
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'assertions-latency' }" data-intercom-target="Sidebar SLA">
              <i class="glyphicon glyphicon-wrench" /> Project Conditions: Latency
            </router-link>
          </li>
          <li>
            <a
              :href="
                getPath({
                  name: 'account_settings_variables',
                  variables: { workspace: 'global' },
                })
              "
              data-intercom-target="Sidebar Env"
            >
              <i class="glyphicon glyphicon-grain" /> Environment Variables
            </a>
          </li>
          <li>
            <router-link :to="{ name: 'token-callback' }">
              <i class="glyphicon glyphicon-lock" /> OAuth Callback Handler
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'files-home' }">
              <i class="glyphicon glyphicon-file" /> Files
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'settings-apikey' }" data-intercom-target="Sidebar API">
              <i class="glyphicon glyphicon-info-sign" /> API Key Access
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-sidebar-con b-border">
        <div
          class="nav-l1"
          @click="toggleList('administration')"
          @keyup.enter="toggleList('administration')"
        >
          <h5>Project Administration</h5>
          <div
            class="caret"
            :style="
              visibleLists.administration ? 'transform: rotate(180deg)' : 'transform: rotate(0deg)'
            "
          />
        </div>
        <ul :class="{ open: visibleLists.administration }" class="nav nav-sidebar nav-sidebar-list">
          <li>
            <router-link :to="{ name: 'project-settings' }">
              <i class="glyphicon glyphicon-cog" /> Users and Admins
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'audits-view' }">
              <i class="glyphicon glyphicon-check" /> Audit Logs
            </router-link>
          </li>
          <li>
            <router-link :to="{ name: 'manage' }">
              <i class="glyphicon glyphicon-cog" /> Project Export / Import
            </router-link>
          </li>
        </ul>
      </div>

      <div v-if="expandedClass && isProjectEditor" class="visible-xs-block">
        <a
          class="expand-button"
          @click.prevent="expandedClass = ''"
          @keyup.enter="expandedClass = ''"
          >More...</a
        >
      </div>
    </div>

    <div v-else-if="!$auth.isAuthenticated">
      <ul class="nav nav-sidebar">
        <li>
          <a @click.prevent="doSignUp" @keyup.enter="doSignup"> Sign Up </a>
        </li>
        <li>
          <a @click.prevent="doLogin" @keyup.enter="doLogin"> Login </a>
        </li>
      </ul>
    </div>
  </GridCol>
</template>

<script>
import { get, call } from 'vuex-pathify';
import FetchMixin from '../fetch_handler';
import newCallPayload from '../js/new_call';

const AUDIENCE = process.env.VUE_APP_AUTH0_AUDIENCE;

export default {
  name: 'SideBar',
  mixins: [FetchMixin],
  data() {
    const storedVisibleLists = JSON.parse(localStorage.getItem('visibleLists')) || {};
    return {
      expandedClass: 'hidden-xs',
      visibleLists: {
        investigate:
          storedVisibleLists.investigate !== undefined ? storedVisibleLists.investigate : true,
        monitoring:
          storedVisibleLists.monitoring !== undefined ? storedVisibleLists.monitoring : true,
        settings: storedVisibleLists.settings || false,
        administration: storedVisibleLists.administration || false,
      },

      projPickerSettings: {
        placeholder: 'Select a project...',
        allowEmpty: true,
      },
      orgPickerSettings: {
        placeholder: 'Select a collection...',
        allowEmpty: true,
      },

      favProjects: [],
      favOrgs: [],

      showProjectStats: false,
    };
  },
  computed: {
    projectsArray() {
      return Object.values(this.projects);
    },
    userHasMultipleProjects() {
      return this.projectsArray.length > 1;
    },
    userRoles() {
      if (!this.$auth.loading && this.$auth.isAuthenticated && this.$auth.user) {
        return this.$auth.user[`${AUDIENCE}/permissions`];
      }
      return [];
    },
    callsPresent() {
      return this.listCalls.length > 0;
    },

    getPath: get('nonVueRoutes/getPath'),
    projectId: get('account2/projectId'),
    loaded: get('account2/loaded'),
    projects: get('account2/projects'),
    organizations: get('account2/organizations'),
    quotaInfo: get('account2/quotaInfo'),
    roles: get('account2/roles'),
    project: get('account2/projects[:projectId'),
    projectName: get('account2/projects[:projectId].project.name'),
    projectOrgId: get('account2/projects[:projectId].project.org_id'),
    projectOrgName: get('account2/organizations[:projectOrgId].name'),
    projectOrgTags: get('account2/organizations[:projectOrgId].system_tags'),
    isProjectEditor: get('account2/isProjectEditor'),
    listCalls: get('apimAPI/listCalls'),
  },
  watch: {
    projectId(val) {
      if (val) {
        this.$nextTick(() => {
          this.fetchData();
        });
      }
    },
  },
  mounted() {
    const storedVisibleLists = JSON.parse(localStorage.getItem('visibleLists'));
    if (storedVisibleLists) {
      this.visibleLists = storedVisibleLists;
    }
    this.fetchData();
  },
  methods: {
    toggleList(list) {
      this.visibleLists[list] = !this.visibleLists[list];
      localStorage.setItem('visibleLists', JSON.stringify(this.visibleLists));
    },
    async fetchData() {
      const claims = await this.$auth.getIdTokenClaims();
      if (!claims) {
        console.log('No claims ', claims);
        return;
      }
      this.favProjects = claims[`${AUDIENCE}/fav_projects`];
      this.favOrgs = claims[`${AUDIENCE}/fav_orgs`];
    },
    doLogin() {
      this.$cookies.set('continue-url', { path: this.$route.path }, '1h');
      this.$auth.loginWithRedirect();
    },
    doSignUp() {
      this.$cookies.set('continue-url', { path: this.$route.path }, '1h');
      this.$auth.loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } });
    },
    saveProject() {},
    async addCall() {
      const { id: callId } = await this.createCall(newCallPayload());
      await this.$router.push({ name: 'calls-edit', params: { callId } });
    },
    setProject: call('account2/setProject'),
    ...call('apimAPI', ['createCall']),
  },
};
</script>

<style scoped>
.nav-top-border {
}
.disabled {
  opacity: 0.5;
  pointer-events: none;
  & a {
    color: #fff;
  }
}

.sidebar {
  padding: 8px 0px !important;
  max-width: 300px;
  overflow-y: auto;
  height: calc(100vh - 78px);
  scrollbar-color: var(--grey) var(--darkpurple);
  scrollbar-width: thin;
}

.sidebar::-webkit-scrollbar {
  width: 8px;
  padding-right: 8px;
}

.sidebar::-webkit-scrollbar-track {
  background: transparent;
  margin-right: 8px;
  padding-right: 8px;
}

.sidebar::-webkit-scrollbar-thumb {
  background-color: var(--grey);
  border-radius: 10px;
}

.nav-l1 {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
}

.caret {
  color: var(--white);
}

.nav {
  padding-bottom: 4px;
}

.nav-sidebar-list {
  width: 100%;
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transform: scaleY(0);
  transform-origin: top;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease,
    transform 0.3s ease;
}

.nav-sidebar-list.open {
  width: 100%;
  max-height: 500px;
  opacity: 1;
  transform: scaleY(1);
}

.nav-sidebar {
  border-bottom: none !important;
  margin-bottom: 0px;
  width: 100%;
  margin-right: 0px;
  margin-left: 0px;
}

.nav-sidebar-con {
  padding-bottom: 4px;
  padding-top: 4px;
}

.b-border {
  border-top: 1px solid var(--border);
}

.nav > li {
  width: 100%;
}
</style>
